import { BorderStyle, BorderThickness, DynamicCssModel } from "../../../models/DynamicCssModel";
import { alphaByPercentage, foregroundColor } from "../../../theme/Mixins";

export function generateTableRowColors(
    dynamicCss: DynamicCssModel,
    theme: object,
): {
    primaryBgColor: string;
    secondaryBgColor: string;
    primaryColor: string;
    secondaryColor: string;
    hoverColor: string;
    hoverBgColor: string;
    selectedColor: string;
    selectedBgColor: string;
} {
    const primaryBgColor = alphaByPercentage(
        theme[dynamicCss.TableRowDefaultTabFillPrimaryColor],
        dynamicCss.TableRowDefaultTabFillPrimaryColorPercent,
    );

    const secondaryBgColor = alphaByPercentage(
        theme[dynamicCss.TableRowDefaultTabFillSecondaryColor],
        dynamicCss.TableRowDefaultTabFillSecondaryColorPercent,
    );

    const hoverTabColor = theme[dynamicCss.TableRowHoverTabColor];

    const hoverBgColor = hoverTabColor
        ? alphaByPercentage(hoverTabColor, dynamicCss.TableRowHoverTabColorPercent)
        : null;
    const selectedBgColor = alphaByPercentage(
        theme[dynamicCss.TableRowSelectedTabColor],
        dynamicCss.TableRowSelectedTabColorPercent,
    );

    const primaryColor = foregroundColor(theme, primaryBgColor);

    const secondaryColor = foregroundColor(theme, secondaryBgColor);
    return {
        primaryBgColor,
        secondaryBgColor,
        primaryColor,
        secondaryColor,
        hoverBgColor,
        hoverColor: foregroundColor(theme, hoverBgColor),
        selectedBgColor,
        selectedColor: foregroundColor(theme, selectedBgColor),
    };
}

export function getBorder(theme: object, dynamicCss: DynamicCssModel) {
    return `${
        dynamicCss.TableRowBorderThickness === BorderThickness.BoldSolid ? "3px" : "1px"
    } solid ${alphaByPercentage(
        theme[dynamicCss.TableRowBorderColor],
        dynamicCss.TableRowBorderColorPercent,
    )} !important`;
}

export function createCssForGridBorder(theme: object, dynamicCss: DynamicCssModel): string {
    // eslint-disable-next-line default-case
    let css = "";
    const border = getBorder(theme, dynamicCss);
    if (BorderStyle.Outside === dynamicCss.TableRowBorderStyle) {
        css += `
        .grid-view tr.data-row { border-left: ${border}; border-right: ${border}; }
        .grid-view .header-row + .data-row { border-top: ${border}; }
        .grid-view tr.data-row:last-child { border-bottom: ${border}; }
        `;
    }

    if (BorderStyle.Vertical === dynamicCss.TableRowBorderStyle) {
        css += `.grid-view tr.data-row td:not(:last-child), .grid-view tr.data-row th.grid__row-header { 
            border: none;
            border-right: ${border}; 
        }`;
    }

    if (BorderStyle.Horizontal === dynamicCss.TableRowBorderStyle) {
        css += `.grid-view tr.data-row:not(:last-child) td, .grid-view tr.data-row:not(:last-child) th.grid__row-header { 
            border: none;
            border-bottom: ${border}; 
        }`;
    }

    if (BorderStyle.All === dynamicCss.TableRowBorderStyle) {
        css += `.grid-view { border: ${border}; }
        .grid-view th, .grid-view td { border: ${border}; }`;
    }

    return css;
}

export function createCssForGridDataRowBackground(
    dynamicCss: DynamicCssModel,
    primaryColor: string,
    secondaryColor: string,
    primaryBg: string,
    secondaryBg: string,
    hoverColor: string,
    hoverBg: string,
    selectedColor: string,
    selectedBg: string,
): string {
    const isDuoChrome = dynamicCss.TableRowDefaultTabBackground === "duoChrome";
    let hoverStyles = "";
    if (hoverBg) {
        hoverStyles = `
        .grid-view tr.data-row:hover { 
            background-color: ${hoverBg} !important; 
            color: ${hoverColor} !important; 
        }`;
    }

    return `
        .grid-view {
            width: 100%;
            border-collapse: collapse !important;
            margin: 0px !important;
        }
        .grid-view tr.data-row > :first-child { border-left:none !important; } 
        .grid-view tr.data-row > :last-child { border-right:none !important; }
        .grid-view tr.data-row:nth-child(even) { 
            background-color: ${isDuoChrome ? secondaryBg : primaryBg} !important; 
            color: ${isDuoChrome ? secondaryColor : primaryColor} !important; 
        }
        .grid-view tr.data-row:nth-child(odd) { background-color: ${primaryBg} !important;  color: ${primaryColor} !important; }
        .grid-view tr.data-row.row--selected { background-color: ${selectedBg} !important; color: ${selectedColor} !important; }
        .grid-view th,.grid-view td { background-color:transparent !important; color:inherit !important;}
        ${hoverStyles}    
        `;
}
